import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCurrentStaff, StaffUser } from "@/Calls/callStaffAuth";
import { Building2, FileText, CreditCard, Truck, Archive, Shield, Loader2, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";

const quickLinks = [
    {
        label: "Insurance Companies",
        description: "Manage carriers, approve accounts, view lead charges",
        to: "/admin/insurance-companies",
        icon: Shield,
        color: "text-blue-600",
        bg: "bg-blue-50",
    },
    {
        label: "Warranty Companies",
        description: "Manage providers, approve accounts, view billing",
        to: "/admin/warranty-companies",
        icon: FileText,
        color: "text-purple-600",
        bg: "bg-purple-50",
    },
    {
        label: "Transport Companies",
        description: "Manage carriers, link to dealers, update status",
        to: "/admin/transport-companies",
        icon: Truck,
        color: "text-indigo-600",
        bg: "bg-indigo-50",
    },
    {
        label: "Salvage Companies",
        description: "Manage buyers, approve accounts, track bids",
        to: "/admin/salvage-companies",
        icon: Archive,
        color: "text-orange-600",
        bg: "bg-orange-50",
    },
    {
        label: "Floor Plan Companies",
        description: "Manage lenders, dealer credit lines, inventory",
        to: "/admin/floorplan-companies",
        icon: CreditCard,
        color: "text-emerald-600",
        bg: "bg-emerald-50",
    },
    {
        label: "Billing & Invoices",
        description: "Recurring invoices, autocharge settings, history",
        to: "/admin/billing",
        icon: CreditCard,
        color: "text-green-600",
        bg: "bg-green-50",
    },
    {
        label: "Admin Center",
        description: "Full admin dashboard with system overview",
        to: "/admin/dashboard",
        icon: Building2,
        color: "text-gray-600",
        bg: "bg-gray-100",
    },
];

export default function AssistantDashboardPage() {
    const [staff,   setStaff]   = useState<StaffUser | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getCurrentStaff()
            .then((r) => { if (r.ok && r.staff) setStaff(r.staff); })
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">
                    {loading
                        ? <span className="inline-flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</span>
                        : staff
                            ? `Welcome, ${staff.first_name}`
                            : "Back Office Dashboard"
                    }
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                    {staff ? `Back Office · ${staff.role.replace(/_/g, " ")}` : "System administration and partner management"}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {quickLinks.map(({ label, description, to, icon: Icon, color, bg }) => (
                    <Link key={to} to={to}>
                        <Card className="p-5 hover:shadow-md transition-shadow h-full">
                            <div className="flex items-start gap-4">
                                <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
                                    <Icon className={`h-5 w-5 ${color}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                        <p className="font-medium text-sm">{label}</p>
                                        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                                </div>
                            </div>
                        </Card>
                    </Link>
                ))}
            </div>
        </div>
    );
}
